import { useContext } from 'react';
import { LtiExerciseModal } from '@pelckmans/business-components/components/lti-exercise-modal';
import { LtiSharedUserMaterial, LtiUserMaterial } from '../../_api/UserMaterialApi';
import { Node } from '../../_api/TableOfContentsApi';
import getAccessToken from '../../../../api/BearerToken';
import { useTranslation } from 'react-i18next';
import usePlatformName from '../../../../hooks/usePlatformName';
import ModalContext from '../../../../contexts/ModalContext';
import ModuleContext from '../../_contexts/ModuleContext';
import MaterialModalHeader from '../../_modals/common/MaterialModalHeader';
import UserMaterialContentType from '../../_components/common/UserMaterialContentType';
import IdentityContext from '../../../../contexts/IdentityContext';
import { buildLtiExerciseMaterial } from '../../../../utils/user-material/buildLtiExerciseMaterial';
import { getModuleById } from '../../_api/ModuleApi';

const tokenAccessor = { tokenGetter: getAccessToken };

const MODAL_ID = 'lti-user-material-modal';

const { REACT_APP_P_STUDIO_LANGUAGE, REACT_APP_LTI_AUTH_URL, REACT_APP_P_STUDIO_GROUP } = process.env;

const getTranslationsForLtiExerciseModal = (t: ReturnType<typeof useTranslation>['t']) => ({
  confirmationModals: {
    title: t('BUSINESS_COMPONENTS.LTI_EXERCISE_MODAL.TITLE'),
    actions: {
      close: t('BUSINESS_COMPONENTS.LTI_EXERCISE_MODAL.ACTIONS.CLOSE'),
      cancel: t('BUSINESS_COMPONENTS.LTI_EXERCISE_MODAL.ACTIONS.CANCEL'),
      restart: t('BUSINESS_COMPONENTS.LTI_EXERCISE_MODAL.ACTIONS.RESTART'),
    },
    body: {
      progression: {
        pending: t('BUSINESS_COMPONENTS.PROGRESSION_BAR.PENDING'),
      },
      currentResult: t('BUSINESS_COMPONENTS.LTI_EXERCISE_MODAL.BODY.CURRENT_RESULT'),
      scorePrefix: t('BUSINESS_COMPONENTS.LTI_EXERCISE_MODAL.BODY.SCORE_PREFIX'),
      notRepeatable: t('BUSINESS_COMPONENTS.LTI_EXERCISE_MODAL.BODY.NOT_REPEATABLE'),
      repeatable: t('BUSINESS_COMPONENTS.LTI_EXERCISE_MODAL.BODY.REPEATABLE'),
    },
  },
  exerciseModal: {
    close: t('BUSINESS_COMPONENTS.LTI_EXERCISE_MODAL.ACTIONS.CLOSE'),
  },
});

export default function useLtiUserMaterial() {
  const { t } = useTranslation();
  const platformName = usePlatformName();

  const { closeModal } = useContext(ModalContext);
  const { selectedModule, module } = useContext(ModuleContext);
  const { identity } = useContext(IdentityContext);

  function handleClose(onModalClosed?: () => Promise<void>) {
    closeModal(MODAL_ID);

    if (onModalClosed) onModalClosed();
  }

  async function openLtiUserMaterial(
    material: LtiUserMaterial | LtiSharedUserMaterial,
    node: Node,
    onModalClosed?: () => Promise<void>,
  ) {
    const materialModule = selectedModule || module || (await getModuleById(material.moduleId));

    if (!materialModule) return;

    return (
      <div id={MODAL_ID} className="material-dialog">
        <LtiExerciseModal
          material={buildLtiExerciseMaterial(material)}
          module={materialModule}
          platformName={platformName}
          tokenAccessor={tokenAccessor}
          ltipsUrl={REACT_APP_LTI_AUTH_URL}
          isOwner={identity.isTeacherAlike}
          studioLanguage={REACT_APP_P_STUDIO_LANGUAGE}
          onClose={() => handleClose(onModalClosed)}
          translations={getTranslationsForLtiExerciseModal(t)}
          headerElement={
            <MaterialModalHeader
              title={material.name}
              description={node.description}
              contentType={<UserMaterialContentType />}
            />
          }
          studioGroup={REACT_APP_P_STUDIO_GROUP}
        />
      </div>
    );
  }

  return { openLtiUserMaterial };
}
